package com.merkost.handpass.admin.container

import com.merkost.handpass.admin.api.AdminRepositoryApi
import com.merkost.handpass.admin.contract.AsyncSection
import com.merkost.handpass.admin.contract.LogAction
import com.merkost.handpass.admin.contract.LogIntent
import com.merkost.handpass.admin.contract.LogState
import com.merkost.handpass.admin.platform.WindowFocusTrigger
import com.merkost.handpass.admin.util.toAdminMessage
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.launch
import pro.respawn.flowmvi.api.Container
import pro.respawn.flowmvi.api.PipelineContext
import pro.respawn.flowmvi.dsl.lazyStore
import pro.respawn.flowmvi.plugins.recover
import pro.respawn.flowmvi.plugins.reduce

@OptIn(FlowPreview::class)
class LogContainer(
    private val repo: AdminRepositoryApi,
    private val focusTrigger: WindowFocusTrigger,
) : Container<LogState, LogIntent, LogAction> {

    private val scope = MainScope()

    private val adminIdFlow = MutableSharedFlow<String>(replay = 1)

    override val store by lazyStore(LogState(), scope) {
        configure { name = "LogStore" }
        recover { e ->
            updateState { copy(data = AsyncSection.Failed(e.toAdminMessage())) }
            null
        }
        reduce { intent ->
            when (intent) {
                is LogIntent.SetActionFilter -> {
                    updateState { copy(actionFilter = intent.value, page = 1) }
                    reload()
                }
                is LogIntent.SetAdminId -> {
                    updateState { copy(adminId = intent.value) }
                    adminIdFlow.tryEmit(intent.value)
                }
                is LogIntent.GoToPage -> {
                    updateState { copy(page = intent.page) }
                    reload()
                }
                is LogIntent.TapTarget -> {
                    val log = intent.log
                    log.targetUserId?.let { action(LogAction.Navigate("/users/$it")) }
                        ?: log.targetListingId?.let { action(LogAction.Navigate("/listings/$it")) }
                }
                is LogIntent.ReactToFocusTick, LogIntent.Refresh -> reload()
                is LogIntent.SetSort -> {
                    updateState { copy(sortBy = intent.sortBy, sortAsc = intent.sortAsc, page = 1) }
                    reload()
                }
                is LogIntent.SetDateFrom -> {
                    updateState { copy(dateFrom = intent.value, page = 1) }
                    reload()
                }
                is LogIntent.SetDateTo -> {
                    updateState { copy(dateTo = intent.value, page = 1) }
                    reload()
                }
            }
        }
    }

    init {
        scope.launch {
            focusTrigger.ticks.collect { tick -> store.intent(LogIntent.ReactToFocusTick(tick)) }
        }
        scope.launch {
            adminIdFlow.debounce(300).distinctUntilChanged().collect { store.intent(LogIntent.GoToPage(1)) }
        }
    }

    private suspend fun PipelineContext<LogState, LogIntent, LogAction>.currentState(): LogState {
        var s = LogState()
        withState { s = this }
        return s
    }

    private suspend fun PipelineContext<LogState, LogIntent, LogAction>.reload() {
        val s = currentState()
        val actionParam = if (s.actionFilter == "All") null else s.actionFilter
        val adminParam = s.adminId.takeIf { it.length == 36 && it.contains('-') }
        updateState { copy(data = AsyncSection.Loading) }
        val resp = repo.getModerationLogs(
            page = s.page,
            pageSize = 20,
            adminId = adminParam,
            action = actionParam,
            sortBy = s.sortBy,
            sortAsc = s.sortAsc,
            dateFrom = s.dateFrom?.let { "${it}T00:00:00Z" },
            dateTo = s.dateTo?.let { "${it}T23:59:59Z" },
        )
        updateState {
            copy(
                data = AsyncSection.Loaded(resp.data),
                totalPages = resp.meta?.totalPages ?: 1,
            )
        }
    }
}
